/**
 * ClientBrandSwitcher — top-nav searchable client picker.
 *
 * Trigger shows the current brand (or "All clients"). The popover is an
 * ARIA combobox: type to filter (matches brand or parent name, accent- and
 * case-insensitive), ↑/↓/Home/End to move, Enter to pick, Esc to close.
 * Lists "All clients", then up to three recently used clients, then every
 * parent brand A–Z with its sub-brands indented underneath.
 *
 * Selecting a brand updates the persisted Zustand store (so the last client
 * is remembered across reloads) AND invalidates all TanStack queries so
 * brand-scoped views (accounts, posts, calendar, inbox) refetch.
 *
 * @module components/ClientBrandSwitcher
 */

import { useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Search, Users } from "lucide-react";
import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import { useClientBrands } from "../api/clientBrands";
import { useClientBrandStore } from "../lib/client-brand";
import type { BrandOption } from "../lib/clientHealth";
import { brandOptions } from "../lib/clientHealth";

interface Row {
  key: string;
  option: BrandOption;
  group: "all" | "recent" | "brands";
}

export function ClientBrandSwitcher() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const queryClient = useQueryClient();

  const selectedBrandId = useClientBrandStore((s) => s.selectedBrandId);
  const recentBrandIds = useClientBrandStore((s) => s.recentBrandIds);
  const setSelectedBrandId = useClientBrandStore((s) => s.setSelectedBrandId);

  const { data, isLoading, isError, refetch } = useClientBrands();
  const brands = useMemo(() => data?.items ?? [], [data]);
  const selectedBrand = selectedBrandId ? brands.find((b) => b.id === selectedBrandId) : null;

  // A remembered client that is no longer listed (removed from social or
  // archived) would keep scoping every view while the trigger says "All
  // clients" — drop it once the list has loaded.
  useEffect(() => {
    if (data && selectedBrandId && !brands.some((b) => b.id === selectedBrandId)) {
      setSelectedBrandId(null);
      queryClient.invalidateQueries();
    }
  }, [data, brands, selectedBrandId, setSelectedBrandId, queryClient]);

  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    const q = query.trim();
    if (!q) {
      out.push({
        key: "all",
        group: "all",
        option: { id: null, name: "All clients", depth: 0, parentName: null, color: null },
      });
      const byId = new Map(brands.map((b) => [b.id, b]));
      for (const id of recentBrandIds ?? []) {
        const b = byId.get(id);
        if (b) {
          out.push({
            key: `recent-${b.id}`,
            group: "recent",
            option: { id: b.id, name: b.name, depth: 0, parentName: null, color: b.primary_color },
          });
        }
      }
    }
    for (const o of brandOptions(brands, q)) out.push({ key: `brand-${o.id}`, group: "brands", option: o });
    return out;
  }, [brands, recentBrandIds, query]);

  function close(returnFocus = true) {
    setOpen(false);
    setQuery("");
    if (returnFocus) triggerRef.current?.focus();
  }

  function select(id: string | null) {
    setSelectedBrandId(id);
    queryClient.invalidateQueries();
    close();
  }

  // Focus the search box and start on the current selection when opening
  // (only on open — typing resets `active` in the input's onChange).
  // biome-ignore lint/correctness/useExhaustiveDependencies: run once per open, not on every rows change
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const idx = rows.findIndex((r) => r.group !== "recent" && r.option.id === selectedBrandId);
    setActive(idx >= 0 ? idx : 0);
  }, [open]);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Keep the active option scrolled into view.
  useEffect(() => {
    if (!open) return;
    const el = document.getElementById(`${listId}-opt-${active}`);
    el?.scrollIntoView?.({ block: "nearest" });
  }, [active, open, listId]);

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (rows.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % rows.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + rows.length) % rows.length);
    } else if (e.key === "Home") {
      e.preventDefault();
      setActive(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActive(rows.length - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = rows[Math.min(active, rows.length - 1)];
      if (row) select(row.option.id);
    }
  }

  const triggerLabel = isLoading ? "Loading…" : selectedBrand ? selectedBrand.name : "All clients";
  const recentCount = rows.filter((r) => r.group === "recent").length;

  return (
    <div ref={ref} className="relative" data-help-context="social-client-switcher">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        // Label-in-name (WCAG 2.5.3): the accessible name starts with the visible
        // selection, so screen readers hear which client is active.
        aria-label={`Client: ${triggerLabel}, change`}
        onClick={() => (open ? close(false) : setOpen(true))}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        // Below sm the decorative icons are dropped, padding tightened and the
        // label capped at max-w-20 (~50px saved with a client selected) so the
        // shell header's app title isn't squeezed to "S…" (live QA R8).
        className="flex items-center gap-1.5 rounded-md border border-light bg-surface px-2 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus sm:px-3 dark:text-gray-200 dark:hover:bg-gray-700"
      >
        <Users className="hidden h-4 w-4 text-gray-400 sm:block dark:text-gray-500" aria-hidden="true" />
        <span className="max-w-20 truncate sm:max-w-40">{triggerLabel}</span>
        <ChevronDown
          className={`hidden h-3.5 w-3.5 text-gray-400 transition-transform sm:block dark:text-gray-500 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-1 w-72 max-w-[calc(100vw-1rem)] rounded-lg border border-light bg-surface shadow-lg">
          <div className="flex items-center gap-2 border-b border-light px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded={true}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={rows.length ? `${listId}-opt-${active}` : undefined}
              aria-label="Search clients"
              placeholder="Search clients…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              className="w-full rounded bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus dark:text-gray-100 dark:placeholder:text-gray-500"
            />
          </div>

          <div id={listId} role="listbox" aria-label="Client brands" className="max-h-80 overflow-y-auto py-1">
            {rows.map((row, idx) => {
              const o = row.option;
              const isSelected = row.group !== "recent" && selectedBrandId === o.id;
              const isActive = idx === active;
              const showRecentHeader = row.group === "recent" && rows[idx - 1]?.group !== "recent";
              const showAllHeader = row.group === "brands" && recentCount > 0 && rows[idx - 1]?.group === "recent";
              return (
                <div key={row.key} role="presentation">
                  {showRecentHeader && (
                    <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                      Recent
                    </p>
                  )}
                  {showAllHeader && (
                    <p className="border-t border-gray-100 px-3 pb-1 pt-2 text-xs font-semibold uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                      All clients
                    </p>
                  )}
                  {/* biome-ignore lint/a11y/useKeyWithClickEvents: keyboard selection is handled by the combobox input (aria-activedescendant pattern). */}
                  <div
                    id={`${listId}-opt-${idx}`}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={-1}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => select(o.id)}
                    className={`flex w-full cursor-pointer items-center gap-2 text-left text-sm transition-colors ${
                      o.depth === 1 ? "py-1.5 pl-8 pr-3" : "px-3 py-2"
                    } ${isActive ? "bg-gray-100 dark:bg-gray-700" : ""} ${
                      isSelected
                        ? "font-semibold text-brand dark:text-brand"
                        : o.depth === 1
                          ? "text-gray-600 dark:text-gray-300"
                          : "font-medium text-gray-800 dark:text-gray-100"
                    }`}
                  >
                    {o.id === null ? (
                      <Users className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                    ) : (
                      <span
                        className={`shrink-0 rounded-full ${o.depth === 1 ? "h-1.5 w-1.5" : "h-2 w-2"} ${
                          // @allow-css-var --brand-swatch-color is the brand's own hex, set inline below.
                          o.color ? "bg-[var(--brand-swatch-color)]" : "bg-gray-300 dark:bg-gray-600"
                        }`}
                        style={o.color ? ({ "--brand-swatch-color": o.color } as React.CSSProperties) : undefined}
                        aria-hidden="true"
                      />
                    )}
                    <span className="truncate">{o.name}</span>
                    {query.trim() && o.depth === 1 && o.parentName && (
                      <span className="ml-auto shrink-0 truncate text-xs text-gray-500 dark:text-gray-400">
                        {o.parentName}
                      </span>
                    )}
                    {isSelected && <Check className="ml-auto h-4 w-4 shrink-0" aria-hidden="true" />}
                  </div>
                </div>
              );
            })}
          </div>

          {isError ? (
            <div
              className="flex items-center justify-between gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400"
              role="alert"
            >
              Couldn't load clients
              <button type="button" onClick={() => refetch()} className="text-xs font-medium underline">
                Retry
              </button>
            </div>
          ) : (
            !isLoading &&
            (brands.length === 0 ? (
              <p className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No brands yet</p>
            ) : (
              query.trim() &&
              rows.length === 0 && (
                <p className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No clients match “{query.trim()}”</p>
              )
            ))
          )}
        </div>
      )}
    </div>
  );
}
